// $FlowFixMe
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { Button } from 'react-bootstrap';
import { bindActionCreators } from 'redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Spinner } from '@fortress-technology-solutions/fortress-component-library/Atoms';

import confirm from '../../../../../../components/ConfirmDialogModal';
import ElementWithPermissions from '../../../../../../components/ElementWithPermissions';
import ConditionalTooltip from '../../../../../../components/ConditionalTooltip';
import { formatProgramList } from '../../../../../../utils/affordable';

import { editLease } from '../../../../actions';

import UploadLeaseModal from '../ButtonActionModals/UploadLeaseModal';
import messages from './messages';

type ManualMethodButtonsProps = {
  allAffordableTabsApproved: boolean,
  applicationId: string,
  canComplete: boolean,
  difference: number,
  isResident: boolean,
  leaseId: string,
  onGenerate?: Function,
  profileId: string,
  transferCertBlocked?: boolean,
  transferCertTooltip?: string,
  unapprovedAffordablePrograms?: Array<string>,
  moveInCertBlocked?: boolean,
  moveInCertTooltip?: string,
};

type ManualMethodButtonsInjectedProps = {
  actions: Object,
  intl: Object,
};

export const ManualMethodButtons = ({
  actions,
  allAffordableTabsApproved,
  applicationId,
  canComplete,
  difference,
  intl,
  isResident,
  leaseId,
  onGenerate,
  profileId,
  selectedProperty,
  transferCertBlocked = false,
  transferCertTooltip = '',
  unapprovedAffordablePrograms = [],
  moveInCertBlocked = false,
  moveInCertTooltip = '',
}: ManualMethodButtonsProps & ManualMethodButtonsInjectedProps) => {
  const [isEditLeaseMode, setIsEditLeaseMode] = useState(false);

  const [showUploadModal, setShowUploadModal] = useState(false);

  const handleShowUploadClick = () => setShowUploadModal(true);
  const handleHideUploadClick = () => setShowUploadModal(false);

  const confirmEditDialog = () => {
    confirm(intl.formatMessage(messages.confirmEditLease)).then(() => {
      actions.editLease(
        leaseId,
        profileId,
        isResident,
        undefined,
        undefined,
        undefined,
        setIsEditLeaseMode,
      );
    });
  };

  const { automateMonthlyLeaseTransactionsMvp: automateMLTsFlag } = useFlags();
  const notReconciled = automateMLTsFlag ? false : difference !== 0;

  // Three gates, one tooltip, each naming its own programs upstream: `transferCertTooltip` from
  // useTransferLeaseCertGate (resident-side), `moveInCertTooltip` from LeaseDataTabForm (the
  // applicant/move-in gate), and the affordable compliance gate formatted here. The `||` chain is
  // the precedence when more than one can name a program: move-in supersedes the tab-surfaced
  // compliance status on the applicant flow (it catches the HUD case that status misses). The
  // compliance generic fallback keeps the copy off a bare "There is an open certification".
  // Branch on the formatted string, not the raw length: formatProgramList drops empty/whitespace
  // names, so a blank entry falls through to the generic copy rather than interpolating nothing.
  const complianceProgramList = formatProgramList(unapprovedAffordablePrograms);
  const complianceGateTooltip = complianceProgramList
    ? intl.formatMessage(messages.certificationsNotApproved, {
        programs: complianceProgramList,
      })
    : intl.formatMessage(messages.certificationsNotApprovedGeneric);
  const certGateTooltip =
    transferCertTooltip || moveInCertTooltip || complianceGateTooltip;
  const showCertGateTooltip =
    !allAffordableTabsApproved || moveInCertBlocked || transferCertBlocked;

  return (
    <>
      <UploadLeaseModal
        applicationId={applicationId}
        isResident={isResident}
        leaseId={leaseId}
        onHide={handleHideUploadClick}
        profileId={profileId}
        show={showUploadModal}
      />
      <ElementWithPermissions scope={['lease-create']}>
        <Button
          className="btn btn-tertiary"
          onClick={confirmEditDialog}
          disabled={isEditLeaseMode}
          data-testid={'editButton'}
        >
          {isEditLeaseMode ? (
            <Spinner small />
          ) : (
            intl.formatMessage(messages.edit)
          )}
        </Button>
        {!isEditLeaseMode && (
          <ConditionalTooltip
            containerSX={{
              display: 'inline-block',
              // `.form-footer .btn`'s `margin-right: 3%` is a percentage, so it resolves
              // against the button's containing block. This shrink-to-fit container becomes
              // that block, collapsing the gap to ~0 and leaving the gated buttons flush
              // against each other. Carry the spacing here — where 3% still resolves against
              // `.button-group` — and neutralise it on the button. `&&` outweighs
              // `.form-footer .btn` without needing `!important`.
              marginRight: '3%',
              '&& .btn': { marginRight: 0 },
            }}
            show={showCertGateTooltip}
            title={certGateTooltip}
            placement={'top'}
            variant={'light'}
          >
            {/* span wrapper: a disabled MUI button swallows pointer events. ElementWithPermissions
                goes inside the span (not the outer wrapper above) so this Button stays its direct
                cloned child and keeps receiving the permission-driven `disabled` — wrapping it in
                ConditionalTooltip first would otherwise swallow that clone (see
                UnselectedMethodSaveButton.js). */}
            <ElementWithPermissions scope={['lease-create']}>
              <Button
                className="btn btn-shout"
                onClick={handleShowUploadClick}
                disabled={
                  !canComplete ||
                  notReconciled ||
                  !allAffordableTabsApproved ||
                  moveInCertBlocked ||
                  transferCertBlocked
                }
                data-testid={'uploadSignedLeaseButton'}
              >
                {intl.formatMessage(messages.uploadSignedLease)}
              </Button>
            </ElementWithPermissions>
          </ConditionalTooltip>
        )}
        {onGenerate && !isEditLeaseMode && (
          <ConditionalTooltip
            containerSX={{
              display: 'inline-block',
              // `.form-footer .btn`'s `margin-right: 3%` is a percentage, so it resolves
              // against the button's containing block. This shrink-to-fit container becomes
              // that block, collapsing the gap to ~0 and leaving the gated buttons flush
              // against each other. Carry the spacing here — where 3% still resolves against
              // `.button-group` — and neutralise it on the button. `&&` outweighs
              // `.form-footer .btn` without needing `!important`.
              marginRight: '3%',
              '&& .btn': { marginRight: 0 },
            }}
            show={showCertGateTooltip}
            title={certGateTooltip}
            placement={'top'}
            variant={'light'}
          >
            {/* span wrapper: a disabled MUI button swallows pointer events. ElementWithPermissions
                goes inside the span for the same reason as Upload Signed Lease above. */}
            <ElementWithPermissions scope={['lease-create']}>
              <Button
                className="btn btn-primary"
                disabled={
                  !canComplete ||
                  notReconciled ||
                  !allAffordableTabsApproved ||
                  moveInCertBlocked ||
                  transferCertBlocked
                }
                onClick={onGenerate}
                data-testid={'downloadLeaseButton'}
              >
                {intl.formatMessage(messages.downloadLease)}
              </Button>
            </ElementWithPermissions>
          </ConditionalTooltip>
        )}
      </ElementWithPermissions>
      {notReconciled && (
        <div className="text-center" style={{ marginRight: '3%' }}>
          {intl.formatMessage(messages.notReconciled)}
        </div>
      )}
    </>
  );
};

export function mapDispatchToProps(dispatch: any): Object {
  const actions = bindActionCreators({ editLease }, dispatch);
  return { actions };
}

const InjectedManualMethodButtons = injectIntl(ManualMethodButtons);

export default connect(null, mapDispatchToProps)(InjectedManualMethodButtons);
